/* global chrome */
// ESLint: `chrome` is a runtime global injected by Chromium-based browsers
// when an extension declares the page in its `externally_connectable`
// allowlist (which our manifest does for cellcog.ai + localhost). It is
// not a window-scoped property in plain web code, so ESLint's `no-undef`
// rule doesn't recognize it without this declaration.

// Browser Extension Wake Signal
// ─────────────────────────────
// MV3 service workers in Chrome terminate after ~30s of idle. When that
// happens, our native-messaging port to the Desktop app dies and the
// "browser status" indicator turns amber. To re-establish the port, the
// service worker has to wake up and call `chrome.runtime.connectNative()`.
//
// Wake triggers we already have (extension side):
//   - chrome.alarms keepalive (Chrome enforces 1-minute MINIMUM — no faster)
//   - content-script ping from cellcog.ai pages (every 20s by default)
//   - chrome.debugger events from in-flight agent sessions
//
// What this module adds: a PUSH wake signal from the web app to the SW.
// We use the `externally_connectable` API (already permitted in the
// extension manifest for `https://cellcog.ai/*` and `http://localhost:3000/*`)
// to call `chrome.runtime.sendMessage(extensionId, { type: 'CELLCOG_BROWSE_WAKE' })`.
// The SW's `onMessageExternal` handler responds by calling its internal
// `ensureConnected()` to (re)open the native-messaging port.
//
// End-to-end latency: ~500ms vs the 0-60s the SW would otherwise wait on
// chrome.alarms / content-script keepalive.
//
// Trigger surfaces calling this helper:
//   1. HumanComputerContext WS handler when Desktop reports connected but
//      browser_connected is false and an extension is installed somewhere
//   2. BrowseSelector dropdown open in the editor toolbar
//   3. BrowseMyChromePage mount
//
// All three share this single helper. A 5-second debounce prevents
// spamming the SW when multiple triggers fire close together (which is
// common: opening /cowork/browse simultaneously fires triggers 1 and 3).

const DEBOUNCE_MS = 5_000;
let lastWakeAt = 0;

/**
 * Send a wake message to the CellCog Browse extension's service worker.
 *
 * No-op if:
 *   - chrome.runtime.sendMessage is unavailable (non-Chrome browser,
 *     extension not installed in this profile, page outside the
 *     externally_connectable allowlist)
 *   - the extension ID hasn't been advertised via the content script
 *     (`window.__cellcogBrowseExtensionId`)
 *   - we've fired a wake within DEBOUNCE_MS
 *
 * Best-effort: no return value, no awaiting. The SW reads the message
 * asynchronously and calls ensureConnected() — the user-visible state
 * change comes back via the Desktop heartbeat + WebSocket pipeline.
 *
 * @param {string} [reason] - Free-text label for debugging only. Logged in
 *   the SW console; helps trace which surface triggered the wake.
 */
export function wakeBrowserExtension(reason = '') {
  // Guard 1: Chrome-specific API
  if (typeof chrome === 'undefined' || !chrome?.runtime?.sendMessage) return;

  // Guard 2: extension ID must have been advertised by content script
  const id = typeof window !== 'undefined' ? window.__cellcogBrowseExtensionId : null;
  if (!id) return;

  // Guard 3: debounce
  const now = Date.now();
  if (now - lastWakeAt < DEBOUNCE_MS) return;
  lastWakeAt = now;

  try {
    chrome.runtime.sendMessage(
      id,
      { type: 'CELLCOG_BROWSE_WAKE', reason: reason || 'unspecified' },
      () => {
        // Read lastError to suppress Chrome's unchecked-runtime-error
        // warning when the extension isn't there. We don't care about
        // success — the user-visible signal is the Desktop heartbeat
        // updating browser_connected: true on its next round trip.
        void chrome.runtime.lastError;
      },
    );
  } catch {
    // Extension context invalidated, profile mismatch, etc. Best-effort.
  }
}

/**
 * Test seam: reset the internal debounce timer. Only intended for unit
 * tests that exercise multiple consecutive wake calls.
 */
export function _resetWakeDebounce() {
  lastWakeAt = 0;
}
